// MediaViewerProvider.tsx — one viewer instance for the whole app.
//
// Mounted once in ShellLayout. A feed of 40 posts must not mount 40 portals, so any surface
// just calls `useMediaViewer().open(items, index)`.

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { PostImage } from '@/core/models/post';
import { usePreferences } from '@/hooks';
import { MediaViewer } from './MediaViewer';
import { MediaViewerContext, type MediaViewerApi } from './context';

interface ViewerState {
  items: PostImage[];
  index: number;
}

export function MediaViewerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ViewerState | null>(null);
  const { prefs } = usePreferences();

  const open = useCallback((items: PostImage[], index = 0) => {
    if (!items.length) return;
    setState({ items, index: Math.min(Math.max(0, index), items.length - 1) });
  }, []);

  const close = useCallback(() => setState(null), []);

  const api = useMemo<MediaViewerApi>(() => ({ open, close }), [open, close]);

  return (
    <MediaViewerContext.Provider value={api}>
      {children}
      {state && (
        <MediaViewer
          items={state.items}
          index={state.index}
          onIndexChange={(i) => setState((s) => (s ? { ...s, index: i } : s))}
          onClose={close}
          reduceMotion={prefs.reduceMotion}
        />
      )}
    </MediaViewerContext.Provider>
  );
}
